/* eslint-disable camelcase */
import { NextRouter } from 'next/router'
import { pathToRegexp } from 'path-to-regexp'
import type { DashboardUser } from '../../schema/user'

const pathnameToPageTypeAndTitleMap = {
  '/': {
    pageType: 'b2b-portal-dashboard',
    pageTitle: 'De Persgroep Recruiter Dashboard — Dashboard',
  },
  '/account': {
    pageType: 'b2b-portal-account',
    pageTitle: 'De Persgroep Recruiter Dashboard — Mijn Account',
  },
  '/vacatures': {
    pageType: 'b2b-portal-job-overview',
    pageTitle: 'De Persgroep Recruiter Dashboard — Vacatures',
  },
  '/vacatures/cart': {
    pageType: 'b2b-jobposting-cart',
    pageTitle: 'De Persgroep Recruiter Dashboard — Vacature Cart',
  },
  '/vacatures/cart/success': {
    pageType: 'b2b-jobposting-order',
    pageTitle: 'De Persgroep Recruiter Dashboard - Vacature Order',
  },
  '/vacatures/:id': {
    pageType: 'b2b-portal-job-details',
    pageTitle: 'De Persgroep Recruiter Dashboard — Vacature Details',
  },
  '/vacature/:id/credits': {
    pageType: 'b2b-jobposting-credit',
    pageTitle: 'De Persgroep Recruiter Dashboard - Nieuwe Vacature (credits)',
  },
  '/vacature/:id/reporting': {
    pageType: 'b2b-portal-reporting',
    pageTitle: 'Reporting',
  },
  '/vacature/:id/product': {
    pageType: 'b2b-jobposting-product',
    pageTitle: 'De Persgroep Recruiter Dashboard - Nieuwe Vacature (product)',
  },
  '/vacature/:id/:step': {
    pageType: 'b2b-portal-draft',
    pageTitle: 'De Persgroep Recruiter Dashboard — Nieuwe Vacature',
  },
  '/producttegoed': {
    pageType: 'b2b-portal-entitlements',
    pageTitle: 'De Persgroep Recruiter Dashboard — Producttegoed',
  },
  '/webshop': {
    pageType: 'b2b-portal-webshop',
    pageTitle: 'De Persgroep Recruiter Dashboard - Webshop',
  },
  '/webshop/cart': {
    pageType: 'b2b-webshop-cart',
    pageTitle: 'De Persgroep Recruiter Dashboard - Webshop Cart',
  },
  '/webshop/cart/success': {
    pageType: 'b2b-webshop-order',
    pageTitle: 'De Persgroep Recruiter Dashboard - Webshop Order',
  },
  '/webshop/products/:id': {
    pageType: 'b2b-portal-product-detail',
    pageTitle: 'De Persgroep Recruiter Dashboard - Webshop Product',
  },
}

const getPageTypeAndTitle = (router: NextRouter) => {
  const mappedRouteToPathname = Object.keys(pathnameToPageTypeAndTitleMap).find(item => pathToRegexp(item).exec(router.asPath.split('?')[0]))

  if (!mappedRouteToPathname) {
    return {
      pageType: 'b2b-portal-404-page',
      pageTitle: 'De Persgroep Recruiter Dashboard — 404',
    }
  }

  return pathnameToPageTypeAndTitleMap[mappedRouteToPathname]
}

const canPush = () => window.dataLayer != null

export const push = (data: Object) => window.dataLayer.push(data)

export const gtmPushDtapEnvironment = (environment: string) => {
  if (!canPush()) return

  push({ environment })
}

export const gtmPushVisitorData = (user: DashboardUser) => {
  if (!canPush()) return

  push({
    visitor: {
      sf_contact_id: user.referenceId,
      sf_account_id: user.company.referenceId,
      isVerified: user.isVerified,
    },
  })
}

export const gtmPushEcommerceData = (event: string, data: Object) => {
  if (!canPush()) return

  // Clear the previous ecommerce object.
  push({ ecommerce: null })
  push({
    event,
    ecommerce: data,
  })
}

export const gtmPushRequestData = (router: NextRouter, optionalRequestData?: Object) => {
  if (!canPush()) return

  push({
    event: 'request',
    request: {
      ...optionalRequestData,
      ...getPageTypeAndTitle(router),
      urlHostname: 'www.persgroepemploymentsolutions.nl',
      urlPath: router.asPath,
      timestamp: new Date().getTime(),
    },
  })
}

export type Item = {
  item_id: string
  item_name: string
  index?: number
  item_brand?: string
  price?: number
  quantity?: number
}

export type Event = 'view_cart' | 'add_payment_info'

export type ItemsData = {
  event: Event
  currency: string
  value: number
  items: Item[]
}

export const gtmPushItemsData = (data: ItemsData) => {
  push(data)
}
